import React, { useState, useLayoutEffect, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Amplify from 'aws-amplify';
import awsconfig from '../../aws-config';
import { VisaoGeralUchasEbas, EquipmentDrillDown } from '../../components';
import * as s from './styled-dashboard';
import { ZonasClientesMapa } from './content';
import AlertOverlay from './content/pracas-mapa/alert-overlay';

Amplify.configure(awsconfig);

const Dashboard = () => {
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [selectedClient, setSelectedClient] = useState({});
  const [markers, setMarkers] = useState([]);
  // Drill-down: clique numa fatia do donut abre modal listando os
  // equipamentos (UCHAs ou Bombas) naquele status.
  const [drillDown, setDrillDown] = useState(null);
  // Tab ativa no mobile (<=720). Default 'mapa' pra o map montar e
  // popular markers ja na primeira visita — a aba "Alertas" depende
  // de markers carregados.
  const [mobileTab, setMobileTab] = useState('mapa');
  const uchas = useSelector((state) => state.primeiroNivel.uchas);
  const ebas = useSelector((state) => state.primeiroNivel.ebas);
  const dispatch = useDispatch();
  const history = useHistory();

  // Click num alerta no mobile (aba Alertas) — replica a logica de
  // pracas-mapa.handleAlertClick (1 aplicacao -> direto pro plant;
  // varias -> tela da praca).
  const handleMobileAlertClick = (marker) => {
    dispatch({
      type: 'SEGUNDO/SET_PARQUE_SELECIONADO',
      parqueSelecionado: marker,
      clienteSelecionado: marker.customer,
    });
    const appliances = (marker && marker.appliance) || [];
    if (appliances.length === 1) {
      history.push('/dashboard/customer/plant', {
        ...appliances[0],
        ...marker,
      });
    } else {
      history.push('/dashboard/customer', { ...marker.customer });
    }
  };

  useEffect(() => {
    dispatch({ type: 'PARQUE/FETCH_ZONES', isCompany: true });
    dispatch({ type: 'SEGUNDO/RESET' });
    return () => dispatch({ type: 'PRIMEIRO/FECHA_CONEXOES' });
  }, [dispatch]);

  useEffect(() => {
    if (markers.length) {
      dispatch({
        type: 'PRIMEIRO/ABRE_CONEXAO_THINGS',
        parques: markers,
      });
    }
    // Cleanup: fecha conexoes do ciclo anterior quando markers muda.
    // Sem isso, mudar de zona/cliente acumulava WebSockets vivos da
    // selecao antiga ate o unmount geral da tela.
    return () => dispatch({ type: 'PRIMEIRO/FECHA_CONEXOES' });
  }, [dispatch, markers]);

  useLayoutEffect(() => {
    // Throttle via rAF: o evento de resize dispara a cada pixel arrastado
    // — sem o frame budget, o setState entupia o reconciler em telas
    // grandes (CCO TV) e gerava jank visivel no mapa Google.
    let rafId = null;
    const updateSize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => setWindowSize(window.innerWidth));
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  const isWide = windowSize > 1325;
  const isMobile = windowSize <= 720;

  // Mobile (<=720): tabs Alertas / Mapa / Visao Geral, focando uma
  // visao por vez (evita scrolllao com sobreposicoes). Wide e tablet
  // mantem o layout antigo (3-col ou stacked).
  if (isMobile) {
    return (
      <s.Container className="fade-in">
        <s.MobileTabBar>
          <s.MobileTabBtn
            type="button"
            $active={mobileTab === 'alertas'}
            onClick={() => setMobileTab('alertas')}
          >
            Alertas
          </s.MobileTabBtn>
          <s.MobileTabBtn
            type="button"
            $active={mobileTab === 'mapa'}
            onClick={() => setMobileTab('mapa')}
          >
            Mapa
          </s.MobileTabBtn>
          <s.MobileTabBtn
            type="button"
            $active={mobileTab === 'geral'}
            onClick={() => setMobileTab('geral')}
          >
            Visão Geral
          </s.MobileTabBtn>
        </s.MobileTabBar>

        {/* Mapa fica sempre montado pra preservar polling/state dos
            markers (que tambem alimentam a aba Alertas). Quando a aba
            ativa nao e Mapa, o wrapper colapsa pra height:0 sem
            desmontar — assim, no retorno pra aba Mapa o Google Maps
            ja esta inicializado e so reage ao resize do container. */}
        <s.MobileMapWrapper $active={mobileTab === 'mapa'}>
          <ZonasClientesMapa
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            markers={markers}
            setMarkers={setMarkers}
            ccoLayout
          />
        </s.MobileMapWrapper>

        <s.MobilePanel $active={mobileTab === 'alertas'}>
          {/* Standalone — independente do AlertOverlay que vive dentro
              do pracas-mapa. Le os mesmos markers, mas renderiza em
              tela cheia pra ser a vista principal da aba. */}
          <AlertOverlay
            markers={markers}
            onAlertClick={handleMobileAlertClick}
            freshAlarms={{}}
            ackedAlerts={{}}
            $cco
          />
        </s.MobilePanel>

        <s.MobilePanel $active={mobileTab === 'geral'}>
          <s.Card width="100%" height="47vh">
            <VisaoGeralUchasEbas
              tipo="uchas"
              data={uchas}
              color="orange"
              onSliceClick={(sliceName) =>
                setDrillDown({ tipo: 'uchas', sliceName })
              }
            />
          </s.Card>
          <s.Card width="100%" height="49vh">
            <VisaoGeralUchasEbas
              tipo="ebas"
              data={ebas}
              color="blue"
              onSliceClick={(sliceName) =>
                setDrillDown({ tipo: 'ebas', sliceName })
              }
            />
          </s.Card>
        </s.MobilePanel>

        {drillDown && (
          <EquipmentDrillDown
            tipo={drillDown.tipo}
            sliceName={drillDown.sliceName}
            markers={markers}
            onClose={() => setDrillDown(null)}
          />
        )}
      </s.Container>
    );
  }

  return (
    <s.Container className="fade-in">
      <s.Main>
        {isWide ? (
          <>
            {/* TOP: filtros | mapa | alertas — pracas-mapa renderiza
                os 3 internamente em grid 380/1fr/360 quando $cco. */}
            <s.TopRow>
              <ZonasClientesMapa
                selectedClient={selectedClient}
                setSelectedClient={setSelectedClient}
                markers={markers}
                setMarkers={setMarkers}
                ccoLayout
              />
            </s.TopRow>

            {/* BOTTOM: UCHAs | EBAs */}
            <s.BottomRow>
              <VisaoGeralUchasEbas
                tipo="uchas"
                data={uchas}
                color="orange"
                onSliceClick={(sliceName) =>
                  setDrillDown({ tipo: 'uchas', sliceName })
                }
              />
              <VisaoGeralUchasEbas
                tipo="ebas"
                data={ebas}
                color="blue"
                onSliceClick={(sliceName) =>
                  setDrillDown({ tipo: 'ebas', sliceName })
                }
              />
            </s.BottomRow>
          </>
        ) : (
          // Fallback laptop pequeno (720 < w <= 1325) — empilha vertical
          <s.Wrapper column>
            <s.Card
              className="google-api-wrapper"
              width="100%"
              height="80vh"
            >
              <ZonasClientesMapa
                selectedClient={selectedClient}
                setSelectedClient={setSelectedClient}
                markers={markers}
                setMarkers={setMarkers}
                ccoLayout
              />
            </s.Card>
            <s.Card width="100%" height="47vh">
              <VisaoGeralUchasEbas
                tipo="uchas"
                data={uchas}
                color="orange"
                onSliceClick={(sliceName) =>
                  setDrillDown({ tipo: 'uchas', sliceName })
                }
              />
            </s.Card>
            <s.Card width="100%" height="49vh">
              <VisaoGeralUchasEbas
                tipo="ebas"
                data={ebas}
                color="blue"
                onSliceClick={(sliceName) =>
                  setDrillDown({ tipo: 'ebas', sliceName })
                }
              />
            </s.Card>
          </s.Wrapper>
        )}
      </s.Main>

      {drillDown && (
        <EquipmentDrillDown
          tipo={drillDown.tipo}
          sliceName={drillDown.sliceName}
          markers={markers}
          onClose={() => setDrillDown(null)}
        />
      )}
    </s.Container>
  );
};

export default Dashboard;
